import type { JSX } from "react";
import type { Components, ExtraProps } from "react-markdown";

type MdProps<K extends keyof JSX.IntrinsicElements> = JSX.IntrinsicElements[K] & ExtraProps;

// react-markdown passes the hast `node` to custom components; strip it so it is not spread onto the DOM.
const withoutNode = <P extends ExtraProps>(props: P): Omit<P, "node"> => {
  const rest: P = { ...props };
  delete rest.node;
  return rest;
};

export const markdownComponents: Components = {
  a: (props: MdProps<"a">) => (
    <a {...withoutNode(props)} className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer" />
  ),
  p: (props: MdProps<"p">) => <p {...withoutNode(props)} className="mb-4 leading-relaxed" />,
  br: (props: MdProps<"br">) => <br {...withoutNode(props)} />,
  ul: (props: MdProps<"ul">) => <ul {...withoutNode(props)} className="mb-4 ml-6 list-disc" />,
  ol: (props: MdProps<"ol">) => <ol {...withoutNode(props)} className="mb-4 ml-6 list-decimal" />,
  li: (props: MdProps<"li">) => <li {...withoutNode(props)} className="mb-1" />,
  h1: (props: MdProps<"h1">) => <h1 {...withoutNode(props)} className="text-2xl font-bold mb-4 mt-6" />,
  h2: (props: MdProps<"h2">) => <h2 {...withoutNode(props)} className="text-xl font-bold mb-3 mt-5" />,
  h3: (props: MdProps<"h3">) => <h3 {...withoutNode(props)} className="text-lg font-bold mb-2 mt-4" />,
  code: (props: MdProps<"code">) => {
    const isInline = !props.className?.includes("language-");
    return isInline ? (
      <code {...withoutNode(props)} className="bg-gray-100 px-1 py-0.5 rounded text-sm" />
    ) : (
      <code {...withoutNode(props)} className="block bg-gray-100 p-3 rounded text-sm" />
    );
  },
  blockquote: (props: MdProps<"blockquote">) => (
    <blockquote {...withoutNode(props)} className="border-l-4 border-gray-300 pl-4 italic mb-4" />
  ),
};

export const markdownComponentsDark: Components = {
  ...markdownComponents,
  a: (props: MdProps<"a">) => (
    <a {...withoutNode(props)} className="text-blue-300 hover:text-blue-200 underline" target="_blank" rel="noopener noreferrer" />
  ),
  code: (props: MdProps<"code">) => {
    const isInline = !props.className?.includes("language-");
    return isInline ? (
      <code {...withoutNode(props)} className="bg-white/15 px-1 py-0.5 rounded text-sm" />
    ) : (
      <code {...withoutNode(props)} className="block bg-white/15 p-3 rounded text-sm" />
    );
  },
  blockquote: (props: MdProps<"blockquote">) => (
    <blockquote {...withoutNode(props)} className="border-l-4 border-white/40 pl-4 italic mb-4" />
  ),
};
